// AssetDetailsPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import AssetDetailsView from '../components/AssetDetailsView';
import TaskAssignmentWizard from '../components/TaskAssignmentWizard';
import LoadingView from '../../shared/components/LoadingView';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import {
  OPEN_VIEW_PARAM,
  ASSETS_OPEN,
  TASK_ID_PARAM,
  ASSET_TAB_PARAM,
} from '../utils/openViewParams';
import { API_URL } from '../../config/env';

export default function AssetDetailsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [asset, setAsset] = useState();
  const [saving, setSaving] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [showTaskWizard, setShowTaskWizard] = useState(false);
  const { asset_id } = useParams();

  const fetchAsset = useCallback(() => {
    fetchWithAuth(`${API_URL}/v1/admin/assets/${asset_id}.json`)
      .then(res => res.json())
      .then(setAsset);
  }, [asset_id]);

  useEffect(() => {
    fetchAsset();
  }, [fetchAsset]);

  const openView = searchParams.get(OPEN_VIEW_PARAM);
  const taskIdParam = searchParams.get(TASK_ID_PARAM);
  const tabParam = searchParams.get(ASSET_TAB_PARAM);

  useEffect(() => {
    if (!asset || openView !== ASSETS_OPEN.TASK_REPORT) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(OPEN_VIEW_PARAM);
      return next;
    }, { replace: true });
  }, [asset, openView, setSearchParams]);

  const handleUpdate = async (assetData) => {
    setSaving(true);
    setUpdateError(null);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/assets/${asset_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset: assetData }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.errors?.join(', ') || res.statusText);
      }
      fetchAsset();
    } catch (err) {
      setUpdateError(err.message || 'Failed to update asset');
    } finally {
      setSaving(false);
    }
  };

  const handleClearTaskUrl = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(TASK_ID_PARAM);
      return next;
    }, { replace: true });
  };

  if (!asset) {
    return (
      <LoadingView
        symbolClass="bi bi-box-seam"
        title="Loading asset"
        subtitle="Please wait while we fetch asset details."
        className="py-5"
      />
    );
  }

  const completedTasks = [
    ...(asset.tasks || []).filter((t) => !!t.completed_at),
    ...(asset.task_history || []),
  ];
  const legacyOpenTaskId =
    openView === ASSETS_OPEN.TASK_REPORT && completedTasks.length > 0
      ? completedTasks[0].id
      : null;

  const parsedTaskId = taskIdParam && /^\d+$/.test(taskIdParam)
    ? Number(taskIdParam)
    : null;
  const openTaskReportTaskId = parsedTaskId ?? legacyOpenTaskId;

  const initialTab =
    tabParam === 'feedback' || tabParam === 'history' ? tabParam : 'history';

  return (
    <div className="px-4 py-3">
      <AssetDetailsView
        asset={asset}
        onNewTask={() => setShowTaskWizard(true)}
        onUpdate={handleUpdate}
        saving={saving}
        updateError={updateError}
        openTaskReportTaskId={openTaskReportTaskId}
        initialTab={initialTab}
        onClearTaskUrl={handleClearTaskUrl}
      />
      <TaskAssignmentWizard
        show={showTaskWizard}
        onClose={() => setShowTaskWizard(false)}
        initialAssetId={asset.id}
      />
    </div>
  );
}
